import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Empty, Flex, Table, TableColumnsType } from 'antd'
import { MenuProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useTemplatesServiceDeleteSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey
} from 'src/api/queries'
import { ApiError, ContentType } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  DeleteConfirm,
  message,
  TableActionButtons,
  Text
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

type Props = {
  isCounterpart?: boolean
}

export const TemplateContractsList = ({ isCounterpart }: Props) => {
  const { t } = useLocales()
  const pagination = useTablePagination()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userData } = useUser()

  const { id } = useParams()
  const theme = useTheme()

  const queryParams = {
    pageSize: DEFAULT_PER_PAGE,
    pageNumber: Number(pagination.current),
    institutionIds: userData?.currentLegalEntityId
      ? [userData?.currentLegalEntityId]
      : [],
    type: [ContentType.CONTRACT]
  }

  const { data, isLoading } =
    useTemplatesServiceGetSystemTemplateListEndpoint(queryParams)

  const { mutate } = useTemplatesServiceDeleteSystemTemplateEndpoint({
    onError: (error: ApiError) => formErrorHandler(error),
    onSuccess: () => {
      message.success(t('counterparts.template_deleted'))
      queryClient.invalidateQueries({
        queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
      })
    }
  })

  const getActionItems = useMemo(
    () =>
      (contractId: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () =>
              navigate(
                isCounterpart
                  ? paths.counterpart.templateContractEdit(id, contractId)
                  : paths.institutions.templateContractEdit(id, contractId)
              )
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('common.delete')}
                onConfirm={() => mutate({ id: contractId })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'name',
        title: t('counterparts.template_name'),
        dataIndex: 'name'
      },
      {
        key: 'contractNumber',
        title: t('counterparts.contract_number'),
        render: ({ signedOn, content }) => (
          <Flex vertical>
            <Text weight={400}>#{content?.contractNumber}</Text>
            <Text weight={400} color={theme.colorTextTertiary}>
              {t('counterparts.signed_date')}:{' '}
              {dayjs(signedOn).format(dayMonthYearBackSlash)}
            </Text>
          </Flex>
        ),
        width: '15%'
      },
      {
        key: 'startDate',
        width: '10%',
        title: t('counterparts.active_from'),
        render: ({ startDate }) =>
          dayjs(startDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'endDate',
        width: '10%',
        title: t('counterparts.active_to'),
        render: ({ endDate }) => dayjs(endDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'description',
        title: t('counterparts.description'),
        render: ({ description }) => description ?? '-'
      },
      {
        width: 100,
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: ({ id: contractId }) => (
          <TableActionButtons
            items={getActionItems(contractId)}
            justify="center"
          />
        )
      }
    ],
    [t]
  )

  const onAdd = () => {
    navigate(
      isCounterpart
        ? paths.counterpart.templateContractAdd(id)
        : paths.institutions.templateContractAdd(id)
    )
  }

  return (
    <Card>
      <Flex vertical gap={24}>
        <Text size="xxl">{t('counterparts.draft')}</Text>

        {(data?.totalCount ?? 0) > 0 && (
          <Flex justify="end" gap={16}>
            <Button icon={<PlusOutlined />} type="primary" onClick={onAdd}>
              {t('counterparts.add_new_template_contract')}
            </Button>
          </Flex>
        )}
        <Table
          loading={isLoading}
          rowKey="id"
          bordered
          dataSource={data?.data}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
          locale={{
            emptyText: (
              <Empty description={t('counterparts.no_contracts_found')}>
                <Button type="primary" onClick={onAdd}>
                  {t('counterparts.add_new_template_contract')}
                </Button>
              </Empty>
            )
          }}
        />
      </Flex>
    </Card>
  )
}
